import { useCallback } from 'react';
import { InvoiceDetailModel, InvoiceModel, InvoiceStatus } from '@frontend/api-invoices';
import { useLatencyBustPoll } from '@frontend/payments-hooks';
import { isPaidOrPendingInvoice } from '@frontend/payments-invoice-calculations';
import { useGetInvoiceNonQuery, useUpdateInvoiceQueryCache } from '@frontend/payments-invoice-controller';

export const useUpdatePaidRequestPoll = <T extends InvoiceModel | undefined = InvoiceModel>(
  unpaidInvoice: T,

  /**
   * Called when changes have beeen detected. Or when the poll reaches max attempts and no onTimeoutCallback is provided.
   */
  onSuccessCallback: (updatedInvoice: InvoiceDetailModel) => void,

  /**
   * Called when the poll reaches max attempts and no changes are detected.
   */
  onTimeoutCallback?: (unchangedInvoice: InvoiceDetailModel) => void
) => {
  const getInvoice = useGetInvoiceNonQuery(unpaidInvoice?.id);

  getInvoice; // happy typescript, happy life.

  const updateInvoiceCache = useUpdateInvoiceQueryCache(unpaidInvoice?.id);

  const { getPollPromise } = useLatencyBustPoll({
    maxAttempts: 10,
    queryMethod: getInvoice,
    onPollEnd: (data) => {
      if (!isPaidOrPendingInvoice(data) && typeof onTimeoutCallback === 'function') {
        onTimeoutCallback(data);
      } else {
        updateInvoiceCache(data);
        onSuccessCallback(data);
      }
    },
    hasDataChanged: (data, initialData: InvoiceStatus) => {
      if (data?.status === InvoiceStatus.Pending) {
        updateInvoiceCache(data);
      }
      return initialData !== data.status && data?.status !== InvoiceStatus.Pending;
    },
    accessor: (data) => {
      return data?.status;
    },
  });

  const setProcessingAndStartPoll = useCallback(() => {
    if (unpaidInvoice) {
      getPollPromise(unpaidInvoice);
      updateInvoiceCache({ ...unpaidInvoice, status: 'Processing' as InvoiceStatus });
    }
  }, [unpaidInvoice, getPollPromise]);

  return { setProcessingAndStartPoll };
};
